/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import { RouteComponentProps } from "react-router-dom";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import AppLink from "../../../../Basis/Components/AppLink";
import CommandBar from "../../../../Basis/Components/CommandBar";
import CommandBarButton from "../../../../Basis/Components/CommandBarButton";
import InnerView from "../../../../Basis/Components/InnerView";
import MaxWidthTableCell from "../../../../Basis/Components/MaxWidthTableCell";
import ViewMainContent from "../../../../Basis/Components/ViewMainContent";
import { DownloadIcon } from "../../../../Basis/FontAwesome";
import AnyUtil from "../../../../Basis/Utils/AnyUtil";
import DateUtil from "../../../../Basis/Utils/DateUtil";
import useOpen from "../../../../Basis/Utils/useOpen";
import useQuery from "../../../../Basis/Utils/useQuery";
import LadeProvisionenZuAbrechnungsdatum from "../../../../Cloud/Belege/Queries/LadeProvisionenZuAbrechnungsdatum";
import BelegeUrlsBuilder from "../../../../Cloud/Belege/Utils/BelegeUrlsBuilder";
import ErstelleProvisionenCsvExportDialog from "../../Components/ErstelleProvisionenCsvExportDialog";
import { translationKeys, useTranslation } from "../../Internationalisierung/i18nMandanten";

const DIALOGE = {
    EXPORTIEREN: "exportieren",
} as const;

export type MandantBelegProvisionenViewProps = {
    abrechnungsdatum: string,
    rechnungId?: string,
    gutschriftId?: string,
} & RouteComponentProps<{}>;

export default function MandantBelegProvisionenView(props: MandantBelegProvisionenViewProps) {
    const { t, tValue } = useTranslation();
    const { state, map } = useQuery({
        query: LadeProvisionenZuAbrechnungsdatum,
        input: { abrechnungsdatum: DateUtil.toDateInputValue(new Date(props.abrechnungsdatum)) },
    });

    const { isOpen, open, close } = useOpen(...Object.values(DIALOGE));

    return (
        <InnerView>
            <CommandBar
                items={[
                    <CommandBarButton
                        caption={t(translationKeys.mandantBelegProvisionenView.exportieren)}
                        icon={<DownloadIcon />}
                        onClick={() => open(DIALOGE.EXPORTIEREN)}
                        disabled={!!state.loading}
                    />,
                ]}
            />
            <ViewMainContent loading={state.loading}>
                <TableContainer>
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    {t(translationKeys.mandantBelegProvisionenView.beleg)}
                                </TableCell>
                                <TableCell>
                                    {t(translationKeys.mandantBelegProvisionenView.teilnahmen)}
                                </TableCell>
                                <TableCell>
                                    {t(translationKeys.mandantBelegProvisionenView.provisionsbetrag)}
                                </TableCell>
                                <TableCell>
                                    {t(translationKeys.mandantBelegProvisionenView.provisionssatz)}
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {map(provisionen => (
                                provisionen.map(provision => (
                                    <TableRow hover key={provision.provisionId}>
                                        <TableCell>
                                            {!AnyUtil.isNullOrUndefined(provision.belegId) ? (
                                                <AppLink to={BelegeUrlsBuilder.getBelegUrl(provision.belegId, {})}>
                                                    {provision.belegMatchcode}
                                                </AppLink>
                                            ) : (
                                                <>
                                                    {provision.belegMatchcode}&nbsp;{t(translationKeys.mandantBelegProvisionenView.geloescht)}
                                                </>
                                            )}
                                        </TableCell>
                                        <MaxWidthTableCell>{provision.zusatzinformation}</MaxWidthTableCell>
                                        <TableCell>{tValue(provision.provisionsbetrag, "Preis")}</TableCell>
                                        <TableCell>{tValue(provision.provisionssatz, "Prozent")}</TableCell>
                                    </TableRow>
                                ))))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <ErstelleProvisionenCsvExportDialog
                    isOpen={isOpen(DIALOGE.EXPORTIEREN)}
                    onAbgebrochen={() => close(DIALOGE.EXPORTIEREN)}
                    onExportiert={() => close(DIALOGE.EXPORTIEREN)}
                    rechnungId={props.rechnungId}
                    gutschriftId={props.gutschriftId}
                />
            </ViewMainContent>
        </InnerView>
    );
}